import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getStages } from '../../api/admin'

const queueColors: Record<string, string> = {
  media: 'bg-blue-100 text-blue-800',
  llm_fast: 'bg-yellow-100 text-yellow-800',
  llm_quality: 'bg-purple-100 text-purple-800',
  notify: 'bg-green-100 text-green-800',
}

export default function PipelinesPage() {
  const { data: stages, isLoading } = useQuery({
    queryKey: ['admin-stages'],
    queryFn: getStages,
  })

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pipeline Stages</h1>
          <p className="text-gray-500 text-sm mt-1">Configure processing stages and prompts</p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl shadow p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-32"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {stages?.map((stage, index) => (
            <Link
              key={stage.id}
              to={`/admin/pipelines/${stage.id}`}
              className="block bg-white rounded-xl shadow hover:shadow-md transition"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    {/* Step number */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                      stage.is_active ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {index + 1}
                    </div>

                    <div>
                      <h3 className={`text-lg font-semibold ${
                        stage.is_active ? 'text-gray-800' : 'text-gray-400'
                      }`}>
                        {stage.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          queueColors[stage.queue] || 'bg-gray-100 text-gray-600'
                        }`}>
                          {stage.queue}
                        </span>
                        {stage.model && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                            {stage.model.split('/').pop()}
                          </span>
                        )}
                        {!stage.is_active && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs font-medium">
                            Disabled
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="text-gray-400">
                    →
                  </div>
                </div>

                {/* Stage details */}
                {(stage.queue === 'llm_fast' || stage.queue === 'llm_quality') && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Model</p>
                        <p className={`font-medium ${stage.model ? 'text-gray-800' : 'text-red-500'}`}>
                          {stage.model ? stage.model.split('/').pop() : 'Not configured'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Temperature</p>
                        <p className="font-medium text-gray-800">
                          {stage.temperature ?? 'Default'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Max Tokens</p>
                        <p className="font-medium text-gray-800">
                          {stage.max_tokens ?? 'Default'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Prompt</p>
                        <p className={`font-medium ${stage.prompt_template ? 'text-gray-800' : 'text-red-500'}`}>
                          {stage.prompt_template ? 'Configured' : 'Not set'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="mt-8 p-4 bg-gray-50 rounded-xl">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Queue Types</h3>
        <div className="flex flex-wrap gap-3">
          {Object.entries(queueColors).map(([queue, color]) => (
            <div key={queue} className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${color}`}>
                {queue}
              </span>
              <span className="text-sm text-gray-600">
                {queue === 'media' && '- Media processing'}
                {queue === 'llm_fast' && '- Fast LLM (cheap)'}
                {queue === 'llm_quality' && '- Quality LLM (expensive)'}
                {queue === 'notify' && '- Notifications'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
